import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/components/WireIntro.css";

/**
 * Ekta Wire Machines — cinematic opening animation.
 *
 * Visual story (≈3s), driven entirely by SVG stroke line-drawing + CSS:
 *   1) A thick bundle of metallic wires feeds in from the LEFT and funnels…
 *   2) …through the drawing die / capstan rollers, emerging as a single thin wire
 *   3) The thin wire runs perfectly straight and centred…
 *   4) …then curves round to form a complete round metallic wire coil
 *   5) The Ekta brand mark reveals inside the coil
 *   6) The full name "EKTA WIRE MACHINES / PVT. LTD." reveals below.
 *
 * Lightweight, responsive. Shown only on the homepage ("/"), once per session,
 * and always restores page scroll when it finishes. Respects reduced-motion.
 */

// 7 wire strands of the incoming bundle, each converging to the die aperture
// at (104,150). Left ends are spread vertically -> reads as a thick bundle.
const STRANDS = [108, 122, 136, 150, 164, 178, 192].map(
  (y) => `M -50 ${y} L 104 150`
);

// Continuous thin wire: a straight run emerging from the die…
const STRAIGHT = "M 113 150 L 228 150";

// Module-level guard: survives component remounts within a single page load, so
// the intro can never restart / loop (independent of sessionStorage availability).
let INTRO_DONE = false;

export default function WireIntro() {
  // The intro is a homepage-only opening animation. Visitors landing on any
  // internal / Google-indexed URL (e.g. /products/...) load that page directly
  // with no overlay. It also plays only once per browser session.
  const shouldPlay = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (INTRO_DONE) return false;
    try {
      if (window.location.pathname !== "/") return false;
      return !window.sessionStorage.getItem("ekta_intro_played");
    } catch (e) {
      return window.location && window.location.pathname === "/";
    }
  }, []);

  const [visible, setVisible] = useState(shouldPlay);
  const [hiding, setHiding] = useState(false);
  const finishedRef = useRef(false);

  // ALWAYS fully restore scrolling — never leave the page locked.
  const unlock = useCallback(() => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }, []);

  // Completing the intro: restore scroll immediately + start the fade-out.
  const beginFinish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    unlock();
    setHiding(true);
  }, [unlock]);

  // Remove the overlay from the DOM entirely (no invisible layer can remain).
  const removeOverlay = useCallback(() => {
    unlock();
    setVisible(false);
  }, [unlock]);

  useEffect(() => {
    if (!shouldPlay) return undefined;
    INTRO_DONE = true; // never replay for the rest of this page load
    try {
      window.sessionStorage.setItem("ekta_intro_played", "1");
    } catch (e) {
      /* ignore */
    }

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Lock scroll ONLY while the intro is on screen.
    document.body.style.overflow = "hidden";

    // Primary teardown is event-driven (title animationend, below). These timers
    // are safety fallbacks so the overlay ALWAYS clears and scroll is restored,
    // even if animation events don't fire (e.g. reduced-motion, background tab).
    const holdMs = reduced ? 900 : 3400;
    const tFinish = setTimeout(beginFinish, holdMs);
    const tRemove = setTimeout(removeOverlay, holdMs + 1000);

    return () => {
      clearTimeout(tFinish);
      clearTimeout(tRemove);
      unlock(); // safety on early unmount
    };
  }, [shouldPlay, beginFinish, removeOverlay, unlock]);

  // Bulletproof safety net: any time the overlay is not on screen, guarantee the
  // page is scrollable. Runs on mount and whenever `visible` flips to false, and
  // restores scroll on unmount — so overflow:hidden can never persist.
  useEffect(() => {
    if (!visible) {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={`ekta-intro-overlay ${hiding ? "is-hiding" : ""}`}
      role="img"
      aria-label="Ekta Wire Machines Pvt. Ltd."
      data-testid="wire-intro"
      onTransitionEnd={(e) => {
        // When the fade-out finishes, fully remove the overlay.
        if (hiding && e.propertyName === "opacity") removeOverlay();
      }}
    >
      <svg
        className="ekta-intro-svg"
        viewBox="0 0 600 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          {/* Metallic steel sheen. userSpaceOnUse so it also paints perfectly
              horizontal strokes (a horizontal line has a zero-height bbox, which
              would collapse an objectBoundingBox gradient and render invisible). */}
          <linearGradient
            id="ektaSteel"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="95"
            x2="0"
            y2="215"
          >
            <stop offset="0%" stopColor="#767c8e" />
            <stop offset="42%" stopColor="#d2d7e1" />
            <stop offset="56%" stopColor="#6a7082" />
            <stop offset="100%" stopColor="#474c5e" />
          </linearGradient>
          <radialGradient id="ektaRoller" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0%" stopColor="#3a3a6b" />
            <stop offset="100%" stopColor="#212146" />
          </radialGradient>
        </defs>

        {/* ---------------- FEED SIDE (fades out for final frame) --------------- */}
        <g className="ekta-feed">
          {/* Thick incoming wire bundle, funnelling into the die */}
          <g>
            {STRANDS.map((d, i) => (
              <path
                key={i}
                className="ekta-strand"
                d={d}
                pathLength="100"
                style={{ animationDelay: `${i * 0.03}s` }}
              />
            ))}
          </g>

          {/* Drawing die / capstan rollers */}
          <g className="ekta-roller">
            <circle cx="100" cy="123" r="14" fill="url(#ektaRoller)" />
            <circle cx="100" cy="123" r="4.5" fill="#6a6aa0" opacity="0.7" />
            <circle cx="100" cy="177" r="14" fill="url(#ektaRoller)" />
            <circle cx="100" cy="177" r="4.5" fill="#6a6aa0" opacity="0.7" />
          </g>

          {/* Straight thin wire emerging from the die */}
          <path className="ekta-straight" d={STRAIGHT} pathLength="100" />
        </g>

        {/* ---------------- ROUND METALLIC WIRE COIL (hero) -------------------- */}
        {/* A real <circle> gives a seamless coil outline; rotated 180° so the
            draw begins at the left (where the straight wire connects) and wraps round. */}
        <circle
          className="ekta-coil"
          cx="300"
          cy="150"
          r="72"
          pathLength="100"
          transform="rotate(180 300 150)"
        />

        {/* Brand mark ("E" for Ekta) centred inside the coil */}
        <g
          className="ekta-mark"
          stroke="#212146"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        >
          <path d="M 286 128 L 286 172" />
          <path d="M 286 128 L 317 128" />
          <path d="M 286 150 L 310 150" />
          <path d="M 286 172 L 317 172" />
        </g>
      </svg>

      <div className="ekta-intro-title" onAnimationEnd={beginFinish}>
        <span className="ekta-intro-name">EKTA WIRE MACHINES PVT. LTD.</span>
      </div>
    </div>
  );
}
